import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, Plus, Search } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardTitle,
  EmptyState,
  PageHeader,
  SkeletonRow,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
} from '@/components/ui';
import { isApiError } from '@/lib/api/envelope';
import { useAuth } from '@/lib/auth/useAuth';
import { fetchClients, type ClientDto, type ClientStatus } from './api';

const STATUS_TONE: Record<ClientStatus, 'success' | 'warning' | 'danger' | 'neutral' | 'primary'> =
  {
    ACTIVE:     'success',
    ONBOARDING: 'primary',
    INACTIVE:   'warning',
    SUSPENDED:  'danger',
    CANCELLED:  'neutral',
    REVOKED:    'danger',
  };

export function ClientsPage() {
  const { hasAnyRole } = useAuth();
  const navigate = useNavigate();
  const canManage = hasAnyRole([
    'AUTHMATECH_SUPER_ADMIN',
    'AUTHMATECH_ADMIN',
    'AUTHMATECH_OPERATOR',
  ]);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<ClientStatus | ''>('');
  const [page, setPage] = useState(0);

  const query = useQuery({
    queryKey: ['dashboard', 'clients', search, statusFilter, page],
    queryFn: () =>
      fetchClients({
        searchTerm: search || undefined,
        clientStatus: statusFilter || undefined,
        page,
        size: 20,
      }),
  });

  const clients = query.data?.content ?? [];
  const totalPages = query.data?.totalPages ?? 1;
  const totalElements = query.data?.totalElements ?? 0;

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title="Clients"
        subtitle="Manage onboarded clients, verification quotas, and API credentials."
        actions={canManage ? (
          <Button onClick={() => navigate('/clients/new')}>
            <Plus className="h-4 w-4" aria-hidden />
            Onboard client
          </Button>
        ) : undefined}
      />

      {/* Filters */}
      <Card>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-subtle" />
            <input
              type="search"
              placeholder="Search by name or contact…"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              className="h-9 w-full rounded-lg border border-border bg-surface pl-9 pr-3 text-sm outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as ClientStatus | '');
              setPage(0);
            }}
            className="h-9 rounded-lg border border-border bg-surface px-3 text-sm outline-none focus:border-primary-500"
          >
            <option value="">All statuses</option>
            <option value="ONBOARDING">Onboarding</option>
            <option value="ACTIVE">Active</option>
            <option value="INACTIVE">Inactive</option>
            <option value="SUSPENDED">Suspended</option>
            <option value="CANCELLED">Cancelled</option>
            <option value="REVOKED">Revoked</option>
          </select>
        </div>
      </Card>

      {/* Table */}
      <Card noPadding>
        <div className="flex items-center justify-between px-5 py-3.5">
          <CardTitle>
            {query.data ? `${totalElements.toLocaleString()} clients` : 'Clients'}
          </CardTitle>
        </div>

        {query.isLoading ? (
          <div>
            {[0, 1, 2, 3, 4, 5].map((i) => <SkeletonRow key={i} cols={6} />)}
          </div>
        ) : query.isError ? (
          <div className="px-5 py-8">
            <div role="alert" className="flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm dark:border-rose-500/25 dark:bg-rose-500/10">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-rose-500" />
              <div>
                <p className="text-rose-700 dark:text-rose-300">
                  {isApiError(query.error) ? query.error.firstErrorMessage : 'Could not load clients.'}
                </p>
                <button type="button" onClick={() => query.refetch()} className="mt-1 text-xs font-medium text-rose-600 hover:underline dark:text-rose-400">
                  Try again
                </button>
              </div>
            </div>
          </div>
        ) : clients.length === 0 ? (
          <EmptyState message="No clients found. Adjust the search or filters." />
        ) : (
          <>
            <Table>
              <THead>
                <TR>
                  <TH>Client</TH>
                  <TH>Contact</TH>
                  <TH>Status</TH>
                  <TH className="text-right">Balance</TH>
                  <TH>Expiry</TH>
                  <TH>Created</TH>
                </TR>
              </THead>
              <TBody>
                {clients.map((client) => (
                  <ClientRow key={client.id} client={client} />
                ))}
              </TBody>
            </Table>

            {totalPages > 1 && (
              <div className="flex items-center justify-between border-t border-border px-5 py-3">
                <span className="text-xs text-fg-muted">
                  Page {page + 1} of {totalPages}
                </span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
                    Previous
                  </Button>
                  <Button variant="outline" size="sm" disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)}>
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}

function ClientRow({ client }: { client: ClientDto }) {
  const expiryDate = client.balanceExpiryDate ? new Date(client.balanceExpiryDate) : null;
  const isExpired = expiryDate != null && expiryDate < new Date();
  const isLowBalance = client.balance != null && client.balance > 0 && client.balance <= 100;

  return (
    <TR>
      <TD>
        <Link
          to={`/clients/${client.id}`}
          className="font-semibold text-primary-600 hover:underline underline-offset-2 dark:text-primary-400"
        >
          {client.clientName}
        </Link>
        <div className="mt-0.5 font-mono text-[10px] text-fg-subtle">{client.id}</div>
      </TD>
      <TD className="text-fg-muted">{client.contactPersonName}</TD>
      <TD>
        <Badge tone={STATUS_TONE[client.clientStatus]}>{client.clientStatus}</Badge>
      </TD>
      <TD className="text-right">
        <span className={isLowBalance ? 'font-semibold text-amber-600 dark:text-amber-400' : 'font-mono'}>
          {client.balance != null ? client.balance.toLocaleString() : '—'}
        </span>
        {isLowBalance && (
          <span className="ml-1 text-[10px] text-amber-600 dark:text-amber-400">low</span>
        )}
      </TD>
      <TD>
        {expiryDate ? (
          <span className={isExpired ? 'text-rose-600 dark:text-rose-400' : 'text-fg-muted'}>
            {expiryDate.toLocaleDateString()}
            {isExpired && ' · expired'}
          </span>
        ) : (
          <span className="text-fg-subtle">—</span>
        )}
      </TD>
      <TD className="text-fg-muted">
        {client.createdAt ? new Date(client.createdAt).toLocaleDateString() : '—'}
      </TD>
    </TR>
  );
}
